import { ShadowButtonStyled } from '@ui/buttons/Button'
import ClearableSelectWrapper from '@ui/selects/ClearableSelectWrapper'
import React, { useEffect, useState } from 'react'
import { Position, Tree } from '@blueprintjs/core'
import { Icon, Popover } from '@ui'
import isEmpty from 'lodash/isEmpty'
import { IFloor, IFloorDetails } from '@api'
import { Mode } from '@ui/selects/LocationSelect/types'
import useLocations from '@ui/selects/LocationSelect/useLocations'
import useSelection from '@ui/selects/LocationSelect/useSelection'
import { SearchField } from './components'
import { useTreeContents, LocationTreeNodeT, LocationNodeType } from './hooks'
import css from './mapFloorSelector.module.scss'
import LeaseNotifications from 'components/Notifications'
import { navigate } from '@reach/router'
import { useHRLocations } from '@ui/selects/HRLocationSelect'
import { useUser } from '@hooks'
import { get } from 'lodash'
import { legacyMode } from 'App'

interface MapFloorSelectionProps {
  value: string | null
  onChange: (value: IFloor) => void
}

export default function MapFloorSelector({ value: _value, onChange }: MapFloorSelectionProps) {
  const regions = useLocations()
  const value = useSelection(Mode.SINGLE_FLOOR, _value, regions)
  const [selectedFloor, setSelectedFloor] = useState(value)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const {
    treeLocationContents,
    favoriteTreeContents,
    searchQuery,
    handleLocationNodeClick,
    handleLocationNodeCollapse,
    handleLocationNodeExpand,
    handleFavoritesNodeClick,
    handleFavoritesNodeCollapse,
    handleFavoritesNodeExpand,
    handleClearSearch,
    handleSearch,
  } = useTreeContents(regions)

  let selectedValue: React.ReactNode = 'Select'

  const updateFloor = (floor: any) => {
    setSelectedFloor({ ...floor })
  }

  useEffect(() => {
    setSelectedFloor(value)
  }, [value, value?.notifications?.edges])

  const openPopOver = () => {
    setIsPopoverOpen(true)
  }

  const closePopOver = () => {
    setIsPopoverOpen(false)
  }

  const onLocationTreeNodeClick = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    if (treeNode.nodeData?.nodeType === LocationNodeType.Floor && treeNode?.nodeData?.floor) {
      onChange(treeNode?.nodeData?.floor)
      setSelectedFloor(treeNode?.nodeData?.floor)
      closePopOver()
    }

    handleLocationNodeClick(treeNode, nodePath)
  }

  const onFavoriteTreeNodeClick = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    if (treeNode.nodeData?.nodeType === LocationNodeType.Floor && treeNode?.nodeData?.floor) {
      onChange(treeNode?.nodeData?.floor)
      closePopOver()
    }

    handleFavoritesNodeClick(treeNode, nodePath)
  }

  if (_value && !value) selectedValue = 'Select'

  if (value) {
    selectedValue = <FloorButton floor={value} />
  }

  const locations = useHRLocations()
  const user = useUser()
  const preferredLocation = get(user, 'user.employee.preferredLocations', [])
  const locationFilterNote =
    preferredLocation.length === 0 || locations.length === preferredLocation.length

  return (
    <ClearableSelectWrapper className={css.wrapper}>
      <Popover
        minimal
        isOpen={isPopoverOpen}
        position={Position.BOTTOM}
        onClose={closePopOver}
        modifiers={{
          arrow: { enabled: false },
        }}
        usePortal={false}
        content={
          <div className={css.popover_container}>
            <div className={css.wrapper_search_input}>
              <SearchField
                onClickClearSearch={handleClearSearch}
                onChange={handleSearch}
                value={searchQuery}
              />
            </div>
            {!legacyMode && !locationFilterNote ? (
              <div>
                <div className={css.wrapper_search_input}>
                  <Icon name="fa-light fa-circle-exclamation" className={css.alert} />
                  Location filter applied. Check your
                  <span
                    className={css.link}
                    onClick={async () => {
                      navigate('/company_settings/user', {})
                    }}>
                    <u>settings</u>
                  </span>
                  page.
                </div>
              </div>
            ) : null}
            <div className={css.list}>
              <Tree
                contents={favoriteTreeContents}
                onNodeClick={onFavoriteTreeNodeClick}
                onNodeCollapse={handleFavoritesNodeCollapse}
                onNodeExpand={handleFavoritesNodeExpand}
              />

              {isEmpty(treeLocationContents) && searchQuery ? (
                <div className={css.no_found_text}> We couldn't find any matches</div>
              ) : (
                <Tree
                  contents={treeLocationContents}
                  onNodeClick={onLocationTreeNodeClick}
                  onNodeCollapse={handleLocationNodeCollapse}
                  onNodeExpand={handleLocationNodeExpand}
                />
              )}
            </div>
          </div>
        }>
        <ShadowButtonStyled
          className={css.pop_over_button}
          data-test="floor-selector-button"
          style={{ padding: '0 10px', width: 320, backgroundColor: 'white' }}
          ghost={false}
          rightIconName={isPopoverOpen ? 'chevron-up' : 'chevron-down'}
          iconName="location-dot"
          text={selectedValue}
          onClick={openPopOver}
        />
      </Popover>
      {value && (
        <LeaseNotifications
          floor={selectedFloor}
          updateFloor={(floor: any) => updateFloor(floor)}
        />
      )}
    </ClearableSelectWrapper>
  )
}

function FloorButton({ floor }: { floor: IFloorDetails }) {
  return (
    <div>
      <div className={`${css.normal} truncate`}>{`${floor.name}`}</div>
      <div className={`${css.small} truncate`}>
        {floor.building.name || ''}, {floor.building.city || ''}, {floor.building.state || ''},{' '}
        {floor.building.address || ''}
      </div>
    </div>
  )
}
